import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { WalkNoyauBlock } from '@/components/admin/sessions/walk/walk-noyau-block';
import { filterControlClass } from '@/components/admin/versioned/filter-field';
import { Button } from '@/components/ui/button';
import {
  NOYAU_TRIALS_KEY,
  NOYAU_TRIAL_KEY,
  RANGEMENT_TRIALS_KEY,
  fetchNoyauTrial,
  fetchNoyauTrials,
  fetchRangementTrials,
  startNoyauTrial,
} from '@/lib/session-api';
import { costLabel, sessionMoment } from '@/lib/session-labels';
import {
  NOYAU_SOURCE_SESSION_CHOICE,
  NOYAU_TRIAL_REFUSAL_SENTENCE,
  noyauSourceChoices,
  noyauTrialRefusalOf,
  trialFingerprint,
  trialOutcome,
  trialSourceLabel,
} from '@/lib/session-noyau';
import { REVIEW_KEY, fetchSessionReview } from '@/lib/session-review-api';
import { cn } from '@/lib/utils';
import type { NoyauTrial } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';

/**
 * « Lecture à blanc » - the TRIALS of the analyst of the noyau on the session
 * being read (noyau.md §9.4, issues #164 and #167).
 *
 * ## What the button does
 *
 * It plays the analyst on a SOURCE - the matter of this session by default -,
 * with the READING of the protocol's development version - the instrument being
 * tuned -, and shows the six notes in the block of the determination, under the
 * block's trial marks.
 *
 * ## The selector « Source » (relecture.md §11, issue #187)
 *
 * In front of the button: the matter of the session (always, the default), each
 * blank rangement of the session that RENDERED - by its hour and the version of
 * its consigne - and the review once it holds an extract. On the targets of the
 * rangement the analyst then reads the extracts of that source, the scribe's
 * elsewhere. The blank rangements come from the list the section below already
 * keeps (`RANGEMENT_TRIALS_KEY`, so a rangement that settles appears here at
 * once); the review's count is read from the review page's own call and cache key,
 * again at every mount, since
 * the review moves on while the parcours is closed. A read of either that
 * fails only leaves its lines out: the matter of the session is always there.
 * The choice is the screen's, never the URL's: it is a posture of the next
 * press, and the trial it starts says its source for good.
 *
 * The trial is kept with the session once settled, and nothing else is written:
 * neither the session, nor its determination if it has one, nor its cost. So
 * the button is on every session that played a protocol, those of before the
 * noyau included: that is its use.
 *
 * ## Which trial the block draws
 *
 * The newest the session keeps, unless the operator picked another in the list
 * below it - the list appears once there are two, so two readings can be set
 * side by side on one session. A press draws the trial it started.
 *
 * ## Why the screen reads a trial again
 *
 * The analyst may think for minutes and retry once, and the proxy cuts a
 * request at sixty seconds: the start answers at once with the trial PENDING,
 * seeded into its own cache key, and the trial is read again every
 * `TRIAL_POLL_MS` until it settles - then never again, a settled trial being
 * unable to change, and the list is read again to learn it. A read that FAILS
 * stops the reading too, and frees the button: the data it keeps says pending,
 * of a trial a restart of the server lost on its way.
 *
 * ## What a refusal says
 *
 * The refusals are worded from their `code` (`NOYAU_TRIAL_REFUSAL_SENTENCE`), and
 * the two of the section Analyste - a level no objective carries
 * (`analyst-levels-missing`), a reading left empty (`analyst-reading-missing`) - get
 * the way to repair it: the protocol's editor, on its section. A refused press
 * draws no trial under its sentence - the notes would read as the refused
 * attempt's - until one is picked in the list.
 *
 * ## It plays TWO passes (analyste.md §11.4, #196)
 *
 * The first on the source chosen, the second on the replies of the session whatever
 * the source - with the section Analyste of the development version of the protocol
 * and the latest development version of Méthode, at the moment of the press: the
 * instrument being tuned. The list of the trials says, on each row, the fingerprint
 * of the instrument it was read with.
 */

/** How often a running trial is read again: the analyst answers in one to three minutes. */
const TRIAL_POLL_MS = 1500;

export interface WalkNoyauTrialProps {
  readonly sessionId: string;
  /** The protocol the session played - where a missing reading gets written. */
  readonly protocolId: string;
  /** The signed-in operator, the scope of the review's cache key (the page of the review reads the same entry). */
  readonly email: string | null;
}

export function WalkNoyauTrial({ sessionId, protocolId, email }: WalkNoyauTrialProps) {
  const client = useQueryClient();
  // The trial picked in the list, or started here; null draws the newest kept.
  const [picked, setPicked] = useState<string | null>(null);

  const list = useQuery({
    queryKey: NOYAU_TRIALS_KEY(sessionId),
    queryFn: () => fetchNoyauTrials(sessionId),
    refetchOnWindowFocus: false,
  });
  const trials = list.data?.trials ?? [];

  const rangementTrials = useQuery({
    queryKey: RANGEMENT_TRIALS_KEY(sessionId),
    queryFn: () => fetchRangementTrials(sessionId),
    refetchOnWindowFocus: false,
  });
  const reviewCount = useQuery({
    // The very read, and the very key, of the review page: one spelling of `GET /review`.
    queryKey: REVIEW_KEY(email, sessionId),
    queryFn: () => fetchSessionReview(sessionId),
    select: (review) => review.extracts.length,
    retry: false,
    // Read again at every mount: a review written meanwhile must be proposed.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
  const choices = noyauSourceChoices(rangementTrials.data?.trials ?? [], reviewCount.data ?? 0);
  // The key of the choice made; one that has since left the list (a trial
  // purged, a review emptied) falls back on the matter of the session.
  const [sourceKey, setSourceKey] = useState(NOYAU_SOURCE_SESSION_CHOICE.key);
  const choice = choices.find((one) => one.key === sourceKey) ?? NOYAU_SOURCE_SESSION_CHOICE;
  const sourceId = useId();

  const start = useMutation({
    mutationFn: () => startNoyauTrial(sessionId, choice.source),
    onSuccess: (trial) => {
      // Seeded, so the block draws the pending trial at once: the reads that
      // follow only move it forward.
      client.setQueryData(NOYAU_TRIAL_KEY(sessionId, trial.id), trial);
      setPicked(trial.id);
      void client.invalidateQueries({ queryKey: NOYAU_TRIALS_KEY(sessionId) });
    },
  });

  const shownId = start.isError ? null : (picked ?? trials[0]?.id ?? null);
  const shownKey = NOYAU_TRIAL_KEY(sessionId, shownId ?? '');

  const shown = useQuery({
    queryKey: shownKey,
    queryFn: async () => {
      const before = client.getQueryData<NoyauTrial>(shownKey);
      const trial = await fetchNoyauTrial(sessionId, shownId ?? '');
      // Settled since the last read: the list still says it runs.
      if (before?.determination.state === 'pending' && trial.determination.state !== 'pending') {
        void client.invalidateQueries({ queryKey: NOYAU_TRIALS_KEY(sessionId) });
      }
      return trial;
    },
    enabled: shownId !== null,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    // A failed read keeps the data before it, which may say PENDING: the error
    // stops the reading, or a trial the server lost would be asked for every
    // 1.5 s for as long as the tab stays open.
    refetchInterval: (query) =>
      query.state.status !== 'error' && query.state.data?.determination.state === 'pending'
        ? TRIAL_POLL_MS
        : false,
  });

  const running =
    start.isPending || (!shown.isError && shown.data?.determination.state === 'pending');
  const refusal = noyauTrialRefusalOf(start.error);

  return (
    <section data-testid={SESSIONS_TEST_IDS.noyauTrial} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-4 py-3">
        <p className="max-w-[80ch] text-caption text-gray-600">
          L’analyste du noyau lit la source choisie - par défaut la matière de cette session - en
          deux passes, avec la section Analyste de la version de développement du protocole et la
          dernière version de développement de Méthode. Chaque essai est gardé avec la session : ni
          la session, ni sa détermination, ni son coût ne changent.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label
            htmlFor={sourceId}
            className="flex items-center gap-2 font-semibold text-gray-700 text-sm"
          >
            Source
            <select
              id={sourceId}
              data-testid={SESSIONS_TEST_IDS.noyauTrialSource}
              value={choice.key}
              onChange={(event) => setSourceKey(event.currentTarget.value)}
              className={filterControlClass(
                choice.source.kind !== 'session',
                'max-w-72 font-normal',
              )}
            >
              {choices.map((one) => (
                <option key={one.key} value={one.key} data-source={one.source.kind}>
                  {one.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid={SESSIONS_TEST_IDS.noyauTrialRun}
            disabled={running}
            onClick={() => start.mutate()}
          >
            {start.isPending
              ? 'Lancement…'
              : trials.length === 0 && picked === null
                ? 'Lancer l’analyste'
                : 'Relancer l’analyste'}
          </Button>
        </div>
      </div>

      {refusal !== null ? (
        <p
          role="alert"
          data-testid={SESSIONS_TEST_IDS.noyauTrialRefusal}
          data-code={refusal}
          className="flex flex-wrap gap-x-3 gap-y-1 text-caption text-gray-700"
        >
          <span>{NOYAU_TRIAL_REFUSAL_SENTENCE[refusal]}</span>
          {refusal === 'analyst-reading-missing' || refusal === 'analyst-levels-missing' ? (
            // The section Analyste is where both are repaired: a level without an
            // objective, a reading left empty (analyste.md §11.4).
            <Link
              to="/admin/protocoles/$protocolId"
              params={{ protocolId }}
              search={{ vue: 'lecture' }}
              data-testid={SESSIONS_TEST_IDS.noyauTrialRefusalLink}
              className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Ouvrir la section Analyste du protocole
            </Link>
          ) : null}
        </p>
      ) : start.isError ? (
        <p role="alert" className="text-caption text-rouge">
          L’analyste n’a pas pu être lancé. Réessayez.
        </p>
      ) : null}

      {list.isError ? (
        <p role="alert" className="text-caption text-rouge">
          Les essais gardés avec cette session n’ont pas pu être lus.
        </p>
      ) : null}

      {shown.isError ? (
        <p role="alert" className="text-caption text-gray-700">
          Cet essai n’a pas pu aller au bout, sans doute un redémarrage du serveur pendant qu’il
          tournait : relancez l’analyste.
        </p>
      ) : null}

      {/* Not under the sentence above: the data kept by a failed read says pending. */}
      {shown.data === undefined || shown.isError ? null : (
        <WalkNoyauBlock
          // One block per trial: the sections folded and the entries opened on one
          // trial are not a posture to carry over to the next one picked in the list.
          key={shown.data.id}
          sessionId={sessionId}
          determination={shown.data.determination}
          protocol={null}
          trial={shown.data}
        />
      )}

      {trials.length < 2 ? null : (
        <nav
          aria-label="Essais de cette session"
          data-testid={SESSIONS_TEST_IDS.noyauTrialList}
          className="flex flex-col gap-1"
        >
          <h3 className="font-semibold text-caption text-gray-500 uppercase tracking-wide">
            Essais de cette session
          </h3>
          <ul className="flex flex-col divide-y divide-gray-100 rounded-md border border-gray-200 bg-white">
            {trials.map((trial) => {
              const current = trial.id === shownId;
              const fingerprint = trialFingerprint(trial);
              return (
                <li key={trial.id}>
                  <button
                    type="button"
                    data-testid={SESSIONS_TEST_IDS.noyauTrialListItem}
                    data-trial={trial.id}
                    data-state={trial.determination.state}
                    aria-current={current ? 'true' : undefined}
                    onClick={() => {
                      start.reset();
                      setPicked(trial.id);
                    }}
                    className={cn(
                      'flex w-full cursor-pointer flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-2 text-left text-caption hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      current && 'bg-gray-50 font-semibold',
                    )}
                  >
                    <span className="text-gray-600">
                      {sessionMoment(trial.determination.startedAt)}
                    </span>
                    <span>
                      {trial.reading.name} v{trial.reading.versionNumber}
                    </span>
                    {fingerprint === null ? null : (
                      <span
                        data-testid={SESSIONS_TEST_IDS.noyauTrialListFingerprint}
                        className="font-mono text-gray-500 text-xs"
                      >
                        {fingerprint}
                      </span>
                    )}
                    <span
                      data-testid={SESSIONS_TEST_IDS.noyauTrialListSource}
                      data-source={trial.source.kind}
                      className={cn(
                        'text-xs',
                        trial.source.kind === 'session'
                          ? 'text-gray-600'
                          : 'font-semibold text-gray-900',
                      )}
                    >
                      {trialSourceLabel(trial.source)}
                    </span>
                    <span className="text-gray-800">{trialOutcome(trial)}</span>
                    <span className="ml-auto text-gray-600">
                      {costLabel(trial.determination.costEur)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </section>
  );
}
